import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Clock, LogIn, LogOut, Calendar, Users, Filter,
  CheckCircle, XCircle, Coffee, ChevronLeft, ChevronRight
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Attendance = ({ user }) => {
  const [todayRecord, setTodayRecord] = useState(null);
  const [history, setHistory] = useState([]);
  const [teamAttendance, setTeamAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('my-attendance');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const isManager = ['superadmin', 'ceo', 'manager', 'team_lead'].includes(user?.role);

  const fetchTodayAttendance = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/attendance/today`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTodayRecord(response.data);
    } catch (error) {
      console.error('Failed to fetch today attendance:', error);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      
      const response = await axios.get(`${API_URL}/attendance?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(response.data);
    } catch (error) {
      console.error('Failed to fetch attendance history:', error);
    }
  }, [dateFrom, dateTo]);

  const fetchTeamAttendance = useCallback(async () => {
    if (!isManager) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/attendance/team?date=${selectedDate}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeamAttendance(response.data);
    } catch (error) {
      console.error('Failed to fetch team attendance:', error);
    }
  }, [selectedDate, isManager]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchTodayAttendance(), fetchHistory()]);
      if (isManager) {
        await fetchTeamAttendance();
      }
      setLoading(false);
    };
    loadData();
  }, [fetchTodayAttendance, fetchHistory, fetchTeamAttendance, isManager]);

  const handleClockIn = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/attendance/clock-in`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Clocked in successfully!');
      fetchTodayAttendance();
      fetchHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clock in');
    }
  };

  const handleClockOut = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/attendance/clock-out`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Clocked out successfully!');
      fetchTodayAttendance();
      fetchHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clock out');
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      present: { bg: '#D1FAE5', color: '#059669', label: 'Present' },
      absent: { bg: '#FEE2E2', color: '#DC2626', label: 'Absent' },
      on_leave: { bg: '#DBEAFE', color: '#2563EB', label: 'On Leave' },
      half_day: { bg: '#FEF3C7', color: '#D97706', label: 'Half Day' },
      not_clocked_in: { bg: '#F3F4F6', color: '#6B7280', label: 'Not Clocked In' }
    };
    const style = styles[status] || styles.absent;
    return (
      <span style={{
        padding: '4px 12px',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: '600',
        background: style.bg,
        color: style.color
      }}>
        {style.label}
      </span>
    );
  };

  const navigateDate = (direction) => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + direction);
    setSelectedDate(date.toISOString().split('T')[0]);
  };

  if (loading) {
    return (
      <div className="content-area" style={{ padding: '40px', textAlign: 'center' }}>
        <div className="loading-spinner"></div>
        <p style={{ marginTop: '16px', color: 'var(--text-secondary)' }}>Loading attendance...</p>
      </div>
    );
  }

  return (
    <div className="content-area" data-testid="attendance-page">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '28px', fontWeight: '700', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Clock size={32} color="#7077FE" />
            Attendance
          </h1>
          <p style={{ margin: '8px 0 0 0', color: 'var(--text-secondary)' }}>Track your daily attendance</p>
        </div>
      </div>

      {/* Clock In/Out Card */}
      <div className="card" style={{ padding: '24px', marginBottom: '24px', background: 'linear-gradient(135deg, #7077FE15, #10B98115)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '20px' }}>
          <div>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)' }}>
              Today's Attendance
            </h3>
            <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '14px' }}>
              {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
            <div style={{ display: 'flex', gap: '24px', marginTop: '16px' }}>
              <div>
                <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>Clock In</span>
                <p style={{ margin: '4px 0 0 0', fontSize: '18px', fontWeight: '600', color: '#10B981' }}>
                  {formatTime(todayRecord?.clock_in)}
                </p>
              </div>
              <div>
                <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>Clock Out</span>
                <p style={{ margin: '4px 0 0 0', fontSize: '18px', fontWeight: '600', color: '#EF4444' }}>
                  {formatTime(todayRecord?.clock_out)}
                </p>
              </div>
              <div>
                <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>Total Hours</span>
                <p style={{ margin: '4px 0 0 0', fontSize: '18px', fontWeight: '600', color: '#7077FE' }}>
                  {todayRecord?.total_hours ? `${todayRecord.total_hours}h` : '-'}
                </p>
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            {!todayRecord?.clock_in ? (
              <button
                onClick={handleClockIn}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '14px 28px',
                  background: 'linear-gradient(135deg, #10B981, #059669)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '15px',
                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
                }}
                data-testid="clock-in-btn"
              >
                <LogIn size={20} />
                Clock In
              </button>
            ) : !todayRecord?.clock_out ? (
              <button
                onClick={handleClockOut}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '14px 28px',
                  background: 'linear-gradient(135deg, #EF4444, #DC2626)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '15px',
                  boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)'
                }}
                data-testid="clock-out-btn"
              >
                <LogOut size={20} />
                Clock Out
              </button>
            ) : (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '14px 28px',
                background: '#D1FAE5',
                color: '#059669',
                borderRadius: '12px',
                fontWeight: '600',
                fontSize: '15px'
              }}>
                <CheckCircle size={20} />
                Day Completed
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', borderBottom: '1px solid var(--border-color)', paddingBottom: '8px' }}>
        <button
          onClick={() => setActiveTab('my-attendance')}
          style={{
            padding: '10px 20px',
            background: activeTab === 'my-attendance' ? '#7077FE' : 'transparent',
            color: activeTab === 'my-attendance' ? 'white' : 'var(--text-secondary)',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
          data-testid="tab-my-attendance"
        >
          <Calendar size={18} />
          My Attendance
        </button>
        {isManager && (
          <button
            onClick={() => { setActiveTab('team-attendance'); fetchTeamAttendance(); }}
            style={{
              padding: '10px 20px',
              background: activeTab === 'team-attendance' ? '#7077FE' : 'transparent',
              color: activeTab === 'team-attendance' ? 'white' : 'var(--text-secondary)',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            data-testid="tab-team-attendance"
          >
            <Users size={18} />
            Team Attendance
          </button>
        )}
      </div>

      {/* My Attendance Tab */}
      {activeTab === 'my-attendance' && (
        <div className="card" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '16px' }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>
              Attendance History
            </h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Filter size={16} color="var(--text-tertiary)" />
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  background: 'var(--bg-primary)',
                  color: 'var(--text-primary)',
                  fontSize: '13px'
                }}
              />
              <span style={{ color: 'var(--text-tertiary)' }}>to</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  background: 'var(--bg-primary)',
                  color: 'var(--text-primary)',
                  fontSize: '13px'
                }}
              />
              <button
                onClick={fetchHistory}
                className="gradient-btn"
                style={{ padding: '8px 16px', fontSize: '13px' }}
              >
                Apply
              </button>
            </div>
          </div>

          {history.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-tertiary)' }}>
              <Clock size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
              <p>No attendance records found</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
                    <th style={{ padding: '12px', textAlign: 'left', color: 'var(--text-secondary)', fontSize: '13px', fontWeight: '600' }}>Date</th>
                    <th style={{ padding: '12px', textAlign: 'left', color: 'var(--text-secondary)', fontSize: '13px', fontWeight: '600' }}>Clock In</th>
                    <th style={{ padding: '12px', textAlign: 'left', color: 'var(--text-secondary)', fontSize: '13px', fontWeight: '600' }}>Clock Out</th>
                    <th style={{ padding: '12px', textAlign: 'left', color: 'var(--text-secondary)', fontSize: '13px', fontWeight: '600' }}>Total Hours</th>
                    <th style={{ padding: '12px', textAlign: 'left', color: 'var(--text-secondary)', fontSize: '13px', fontWeight: '600' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((record, idx) => (
                    <tr key={record.id || idx} style={{ borderBottom: '1px solid var(--border-color)' }}>
                      <td style={{ padding: '14px 12px', color: 'var(--text-primary)', fontWeight: '500' }}>{formatDate(record.date)}</td>
                      <td style={{ padding: '14px 12px', color: '#10B981' }}>{formatTime(record.clock_in)}</td>
                      <td style={{ padding: '14px 12px', color: '#EF4444' }}>{formatTime(record.clock_out)}</td>
                      <td style={{ padding: '14px 12px', color: '#7077FE', fontWeight: '600' }}>{record.total_hours ? `${record.total_hours}h` : '-'}</td>
                      <td style={{ padding: '14px 12px' }}>{getStatusBadge(record.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Team Attendance Tab */}
      {activeTab === 'team-attendance' && isManager && (
        <div className="card" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '16px' }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>
              Team Attendance
            </h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button
                onClick={() => navigateDate(-1)}
                style={{
                  padding: '8px',
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  color: 'var(--text-primary)'
                }}
              >
                <ChevronLeft size={18} />
              </button>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                style={{
                  padding: '8px 16px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  background: 'var(--bg-primary)',
                  color: 'var(--text-primary)',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              />
              <button
                onClick={() => navigateDate(1)}
                style={{
                  padding: '8px',
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  color: 'var(--text-primary)'
                }}
              >
                <ChevronRight size={18} />
              </button>
              <button
                onClick={() => setSelectedDate(new Date().toISOString().split('T')[0])}
                style={{
                  padding: '8px 16px',
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  color: 'var(--text-primary)',
                  fontWeight: '500',
                  fontSize: '13px'
                }}
              >
                Today
              </button>
            </div>
          </div>

          {/* Summary Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '16px', marginBottom: '24px' }}>
            <div style={{ padding: '16px', background: '#D1FAE520', borderRadius: '10px', textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#10B981' }}>
                {teamAttendance.filter(t => t.status === 'present').length}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Present</div>
            </div>
            <div style={{ padding: '16px', background: '#DBEAFE20', borderRadius: '10px', textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#2563EB' }}>
                {teamAttendance.filter(t => t.status === 'on_leave').length}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>On Leave</div>
            </div>
            <div style={{ padding: '16px', background: '#FEE2E220', borderRadius: '10px', textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#DC2626' }}>
                {teamAttendance.filter(t => t.status === 'absent').length}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Absent</div>
            </div>
            <div style={{ padding: '16px', background: '#F3F4F620', borderRadius: '10px', textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--text-primary)' }}>
                {teamAttendance.length}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Total</div>
            </div>
          </div>

          {teamAttendance.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-tertiary)' }}>
              <Users size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
              <p>No team members found</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
                    <th style={{ padding: '12px', textAlign: 'left', color: 'var(--text-secondary)', fontSize: '13px', fontWeight: '600' }}>Team Member</th>
                    <th style={{ padding: '12px', textAlign: 'left', color: 'var(--text-secondary)', fontSize: '13px', fontWeight: '600' }}>Role</th>
                    <th style={{ padding: '12px', textAlign: 'left', color: 'var(--text-secondary)', fontSize: '13px', fontWeight: '600' }}>Clock In</th>
                    <th style={{ padding: '12px', textAlign: 'left', color: 'var(--text-secondary)', fontSize: '13px', fontWeight: '600' }}>Clock Out</th>
                    <th style={{ padding: '12px', textAlign: 'left', color: 'var(--text-secondary)', fontSize: '13px', fontWeight: '600' }}>Hours</th>
                    <th style={{ padding: '12px', textAlign: 'left', color: 'var(--text-secondary)', fontSize: '13px', fontWeight: '600' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {teamAttendance.map((record, idx) => (
                    <tr key={record.user_id || idx} style={{ borderBottom: '1px solid var(--border-color)' }}>
                      <td style={{ padding: '14px 12px' }}>
                        <div style={{ fontWeight: '500', color: 'var(--text-primary)' }}>{record.user_name}</div>
                        <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>{record.user_email}</div>
                      </td>
                      <td style={{ padding: '14px 12px', color: 'var(--text-secondary)', fontSize: '13px', textTransform: 'capitalize' }}>
                        {record.role?.replace('_', ' ')}
                      </td>
                      <td style={{ padding: '14px 12px', color: '#10B981' }}>{formatTime(record.clock_in)}</td>
                      <td style={{ padding: '14px 12px', color: '#EF4444' }}>{formatTime(record.clock_out)}</td>
                      <td style={{ padding: '14px 12px', color: '#7077FE', fontWeight: '600' }}>
                        {record.total_hours ? `${record.total_hours}h` : '-'}
                      </td>
                      <td style={{ padding: '14px 12px' }}>
                        {getStatusBadge(record.status)}
                        {record.leave_type && (
                          <span style={{ marginLeft: '8px', fontSize: '11px', color: 'var(--text-tertiary)' }}>
                            ({record.leave_type})
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Attendance;
